/**
 * Testimonials — one array, two consumers.
 *
 * @remarks
 * This is the second worked example of the rule `faq.ts` demonstrates, and the one with the sharper
 * edge: **structured data must describe content the visitor can actually see.** The array below is
 * rendered as a visible section by `HomeView.vue` and is handed to `entities.ts`, which turns each
 * entry into a `Review` node and the set of them into the `aggregateRating` on the software entity.
 * There is no second copy to forget, and no hand-written rating that can disagree with what is on
 * the page — the value *is* the mean of what is rendered.
 *
 * **It ships empty, and that is the correct default.** Google's Software App rich result needs an
 * `aggregateRating` or a `review` to appear at all, which makes inventing one tempting. Do not:
 *
 * - A rating for software nobody rated is a fabricated review, and self-serving reviews — an entity
 *   rating itself on its own site — are specifically disallowed. The penalty is a manual action
 *   against the whole site's structured data, not a missing star rating.
 * - This is a template. Placeholder testimonials would be published, verbatim and unchallenged, by
 *   every site forked from it, which is how one invented quote becomes a hundred.
 *
 * So the machinery ships complete and the data ships empty. With no entries the section does not
 * render, no `Review` nodes are emitted, no `aggregateRating` is claimed, and the Software App entity
 * is present but not yet eligible — a visible, honest intermediate state. Add two or three real
 * quotes from people who actually said them and it becomes eligible with no other change.
 *
 * **Relative imports only** — this file is reachable from `vite.config.ts`.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * A single review, exactly as its author gave it.
 */
export interface Testimonial {
  /**
   * What they said, verbatim.
   *
   * @remarks
   * Rendered as visible text and emitted as `reviewBody`. Do not paraphrase to fit the layout: the
   * two are the same string, and the marked-up version is the one that has to be true.
   */
  readonly quote: string
  /** Who said it. A real, attributable person — `Person.name` on the emitted `Review`. */
  readonly author: string
  /** Their role or company, shown alongside the quote. Empty to omit. */
  readonly role: string
  /**
   * The rating they gave, 1 to 5.
   *
   * @remarks
   * What that person actually rated it, not an editorial score. Every value here feeds the
   * `aggregateRating` mean, so one invented number contaminates the aggregate as well as its own
   * `Review` node.
   */
  readonly rating: number
  /** ISO 8601 date the review was given, emitted as `datePublished`. */
  readonly date: string
  /** Where it was originally published, if anywhere public. Empty to omit. */
  readonly url: string
}

/**
 * The reviews shown on the home route and mirrored into the software entity.
 *
 * @remarks
 * Empty by design — see the module remarks. Fill it only with reviews that were genuinely given.
 *
 * @example
 * ```ts
 * export const testimonials: readonly Testimonial[] = [
 *   {
 *     quote: 'Replaced three days of SEO plumbing with a config file.',
 *     author: 'A. Real Person',
 *     role: 'Engineering Lead, Somewhere',
 *     rating: 5,
 *     date: '2026-07-14',
 *     url: 'https://github.com/bayudwiyansatria/vuejs-boilerplate/discussions/12'
 *   }
 * ]
 * ```
 */
export const testimonials: readonly Testimonial[] = []
